import { useTranslation } from 'react-i18next';

import type { Label } from '@/entities/board';
import { formatCalendarDate } from '@/shared/lib/datetime';

import styles from './Agenda.module.css';
import { barStyle, itemFill, labelColorsOf } from './labels';
import {
  type CalendarEvent,
  type DateRange,
  type DayLoad,
  eachDay,
  eventsOnDay,
  splitLocal,
} from './model';
import { StateTag } from './StateTag';
import { Workload } from './Workload';

interface Props {
  events: readonly CalendarEvent[];
  range: DateRange;
  /** The days to list (default: every day of the range); a Work week may skip some. */
  days?: readonly string[];
  today: string;
  locale: string;
  colorOf: (boardId: string) => string;
  boardNameOf: (boardId: string) => string;
  /** Labels of the boards in scope: their colors paint the item's bar. */
  labels?: readonly Label[];
  /** Daily workload next to each date (null / absent = off). */
  workload?: ReadonlyMap<string, DayLoad> | null;
  /** Adds a task on that day (hidden when the user cannot edit). */
  onCreateDay?: (day: string) => void;
  onOpenCard: (cardId: string) => void;
}

function timeLabel(event: CalendarEvent, day: string, allDayText: string): string {
  if (event.allDay) return allDayText;
  if (event.kind === 'deadline') return event.start.slice(11, 16);
  const start = event.start.slice(0, 10) === day ? event.start.slice(11, 16) : '00:00';
  const end = event.end ? splitLocal(event.end) : null;
  const endText = end && end.date === day ? end.time : end ? '24:00' : '';
  return endText ? `${start} – ${endText}` : start;
}

/**
 * The same period as a plain list of days: the phone presentation of Week / Work week
 * (and Day), where an hour grid would not fit. Deadline-only tasks appear on their day.
 */
export function Agenda({
  events,
  range,
  days,
  today,
  locale,
  colorOf,
  boardNameOf,
  labels = [],
  workload = null,
  onCreateDay,
  onOpenCard,
}: Props) {
  const { t } = useTranslation();
  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' });
  return (
    <div className={styles.agenda} data-testid="calendar-agenda">
      {(days ?? eachDay(range)).map((day) => {
        const items = eventsOnDay(events, day);
        const date = new Date(`${day}T00:00:00Z`);
        return (
          <section
            key={day}
            className={`${styles.day} ${day === today ? styles.today : ''}`}
            aria-label={`${weekday.format(date)}, ${formatCalendarDate(day, locale)}`}
            data-testid="agenda-day"
            data-day={day}
          >
            <header className={styles.header}>
              <h3 className={styles.heading}>
                <span className={styles.weekday}>{weekday.format(date)}</span>
                <span className={styles.date}>{formatCalendarDate(day, locale)}</span>
                <Workload load={workload?.get(day)} compact />
              </h3>
              {onCreateDay && (
                <button
                  type="button"
                  className={styles.add}
                  aria-label={t('calendar.addOnDay', { date: formatCalendarDate(day, locale) })}
                  onClick={() => {
                    onCreateDay(day);
                  }}
                >
                  +
                </button>
              )}
            </header>
            {items.length === 0 ? (
              <p className={styles.empty}>{t('calendar.noTasksDay')}</p>
            ) : (
              <ul className={styles.list}>
                {items.map((event) => {
                  const done = event.card.completed_at !== null;
                  return (
                    <li key={event.id}>
                      <button
                        type="button"
                        className={`${styles.item} ${done ? styles.done : ''} ${event.phase === 'past' && !done ? styles.past : ''}`}
                        data-testid="agenda-item"
                        onClick={() => {
                          onOpenCard(event.id);
                        }}
                      >
                        <span
                          className={styles.bar}
                          style={barStyle(
                            itemFill(labelColorsOf(event.card, labels)),
                            colorOf(event.boardId),
                          )}
                          aria-hidden
                        />
                        <span className={styles.time}>
                          {timeLabel(event, day, t('calendar.allDay'))}
                        </span>
                        <span className={styles.main}>
                          <span className={styles.title}>{event.card.title}</span>
                          <span className={styles.meta}>
                            {[
                              boardNameOf(event.boardId),
                              event.kind === 'deadline' ? t('calendar.deadline') : '',
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                          <StateTag event={event} />
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
